import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewContainerRef
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MbCheckoutStepService } from '../../services/mb-checkout-step.service'
import {
  Address,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  OCC_USER_ID_GUEST,
  SemanticPathService,
  TranslationService,
  UserIdService
} from '@spartacus/core'
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms'
import { MbConstants } from '../../../shared/mb-constants'
import { combineLatest, Subscription } from 'rxjs'
import { CmsComponentData, LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront'
import { MbCheckoutReviewNotesConnector } from '../../connectors/review-notes'
import { mbCheckoutRoutingConfig } from '../../config/mb-checkout-routing-config'
import {
  MbTextareaAutoresizeDirective
} from '../../../shared/directives/mb-textarea-autoresize.directive'
import { MbCheckoutNavigationService } from '../../services/mb-checkout-navigation.service'
import { MbOrderFacade } from '../../facades/mb-order.facade'
import {
  MbBillingInformation,
  MbCheckoutContactDetailsModel,
  MbCheckoutReviewInfoType,
  MbCheckoutReviewModel,
  MbCheckoutTypes
} from '../../../models'
import { mbCheckoutConfig } from '../../config/mb-default-checkout-config'
import { CheckoutStepType } from '../../models'
import { MbSpinnerService } from '../../../shared/services/mb-spinner.service'
import { CheckoutReviewSubmitComponent, CheckoutStepService } from '@spartacus/checkout/base/components'
import {
  CheckoutDeliveryAddressFacade,
  CheckoutDeliveryModesFacade,
  CheckoutPaymentFacade
} from '@spartacus/checkout/base/root'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { OrderFacade, OrderPlacedEvent } from '@spartacus/order/root'
import { withLatestFrom } from 'rxjs/operators'

@Component({
  selector: 'mb-checkout-review',
  template: ''
})
export class MbCheckoutReviewBaseComponent extends CheckoutReviewSubmitComponent implements OnInit, OnDestroy {

  @ViewChildren(MbTextareaAutoresizeDirective) textAreas: QueryList<MbTextareaAutoresizeDirective> | undefined

  infoTypes = MbCheckoutReviewInfoType
  constants = MbConstants
  review: MbCheckoutReviewModel = {}
  reviewForm: UntypedFormGroup = this.convertCheckoutReviewModel2Form()
  shipmentAddress: Address | undefined
  billingInformation: MbBillingInformation | undefined
  subscription: Subscription = new Subscription()
  placedOrder: any
  activeCartID: string | undefined
  contactDetails: MbCheckoutContactDetailsModel | undefined
  checkoutRoutes = mbCheckoutRoutingConfig
  checkoutSteps = mbCheckoutConfig
  isPurchaseCheckoutType = false
  checkoutTypes = MbCheckoutTypes
  userId: string | undefined

  constructor (protected activatedRoute: ActivatedRoute,
               protected mbCheckoutStepService: MbCheckoutStepService,
               protected router: Router,
               protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
               protected checkoutPaymentService: CheckoutPaymentFacade,
               protected activeCartService: ActiveCartFacade,
               protected translation: TranslationService,
               protected checkoutStepService: CheckoutStepService,
               protected checkoutDeliveryModesFacade: CheckoutDeliveryModesFacade,
               protected launchDialogService: LaunchDialogService,
               protected vcr: ViewContainerRef,
               protected checkoutReviewNotesConnector: MbCheckoutReviewNotesConnector,
               protected component: CmsComponentData<any>,
               protected mbCheckoutNavigationService: MbCheckoutNavigationService,
               protected globalMessageService: GlobalMessageService,
               protected mbCheckoutService: MbOrderFacade,
               protected userIdService: UserIdService,
               protected semanticPathService: SemanticPathService,
               protected orderFacade: OrderFacade,
               protected eventService: EventService,
               protected mbSpinnerService?: MbSpinnerService) {
    super(checkoutDeliveryAddressFacade, checkoutPaymentService, activeCartService, translation, checkoutStepService,
      checkoutDeliveryModesFacade)
    this.subscription.add(combineLatest([this.activeCartService.getActive(), this.userIdService.getUserId()])
      .subscribe(([cart, userId]) => {
        const checkoutStatusPage = this.checkoutSteps?.checkout?.steps?.find(step =>
          step?.id === cart?.checkoutStatus?.checkoutPageId)
        const reviewPageStep = this.checkoutSteps?.checkout?.steps?.find(step =>
          step?.type?.includes(CheckoutStepType.REVIEW))?.step
        if (checkoutStatusPage?.step && checkoutStatusPage.step < (reviewPageStep as number)) {
          this.router.navigate([this.semanticPathService.get(checkoutStatusPage?.routeName || 'cart')])
        }
        if (Object.keys(cart).length !== 0) {
          this.review.checkoutNotes = cart.checkoutNotes
          this.reviewForm = this.convertCheckoutReviewModel2Form()
          this.resizeTextArea()
          this.shipmentAddress = cart?.deliveryAddress
          this.billingInformation = cart?.paymentDetails
          this.contactDetails = cart?.contactInformation
          this.activeCartID = cart?.user?.name === OCC_USER_ID_GUEST ? cart?.guid : cart?.code
          const checkoutType = cart?.checkoutStatus?.checkoutType
          this.userId = userId
          if (checkoutType === this.checkoutTypes.CHECKOUT_TYPE_D || checkoutType === this.checkoutTypes.CHECKOUT_TYPE_E) {
            this.isPurchaseCheckoutType= true
          }
        }
        this.mbSpinnerService?.clearMbSpinner()
      }))
    this.mbCheckoutService.stopPlaceOrderTrigger()
  }

  ngOnInit (): void {
    this.subscription.add(
      combineLatest([
        this.eventService.get(OrderPlacedEvent),
        this.mbCheckoutNavigationService.showPaymentProviderPage
      ]).subscribe(([orderPlacedEvent, showPaymentProviderPage]) => {
        if (Object.keys(orderPlacedEvent?.order)) {
          this.launchDialogService.clear(LAUNCH_CALLER.PLACE_ORDER_SPINNER)
          if (showPaymentProviderPage) {
            this.mbCheckoutNavigationService.navigateToPaymentProviderPage()
          } else {
            this.mbCheckoutNavigationService.navigateToConfirmationPage(orderPlacedEvent?.order)
          }
        }
      })
    )

    this.subscription.add(this.mbCheckoutService.isPlaceOrderTriggered
      .subscribe((isPlaceOrderTriggered: boolean) => {
        if (isPlaceOrderTriggered) {
          this.next()
        }
      }))
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
    this.orderFacade.clearPlacedOrder()
  }

  back () {
    this.mbCheckoutStepService.back(this.activatedRoute)
  }

  next () {
    if (!this.reviewForm.valid) {
      this.reviewForm.markAllAsTouched()
    } else {
      this.mbCheckoutNavigationService.setPaymentProviderPageVisibility(
        !this.billingInformation?.paymentMode?.poPaymentMode
        && this.billingInformation?.paymentMode?.code !== this.constants.PLACEHOLDER_PAYMENT_MODE_CODE)
      this.placeOrder()
    }
  }

  convertCheckoutReviewModel2Form () {
    return new FormGroup({
      checkoutNotes: new FormControl(this.review?.checkoutNotes, Validators.maxLength(1000)),
    })
  }

  convertCheckoutReviewForm2Model () {
    this.review.checkoutNotes = this.reviewForm.get(this.constants.CHECKOUT_NOTES_CODE)?.value
  }

  resizeTextArea () {
    setTimeout(() => {
      this.textAreas?.forEach(textarea => {
        textarea.onInput()
      })
    }, 10)
  }

  placeOrder () {
    this.postForm(true)
  }

  postForm (withPlaceOrder: boolean = false) {
    if (!this.reviewForm.valid) {
      this.reviewForm.markAllAsTouched()
    } else {
      this.convertCheckoutReviewForm2Model()

      if (this.activeCartID) {
        this.subscription.add(this.checkoutReviewNotesConnector.post(this.review, this.activeCartID).pipe(
          withLatestFrom(this.mbCheckoutNavigationService.showPaymentProviderPage)
        ).subscribe(([reviewNotesResult, showPaymentProviderPage]) => {
          if (withPlaceOrder) {
            if (showPaymentProviderPage) {
              this.mbCheckoutService.redirectToPaymentPage(true)
            } else {
              this.orderFacade.placeOrder(true)
            }
          }
          },
            (error: Error) => {
              console.error(error)
              // TODO: Spartacus Upgrade - checkpoint after testing
              this.activeCartService.reloadActiveCart()
            }
          ))
      } else {
        this.globalMessageService.add({ key: 'review.error' }, GlobalMessageType.MSG_TYPE_ERROR)
      }
    }
  }
}
